/**
 * MATAFOME — API DE CONFIGURAÇÕES DA LOJA, HORÁRIOS, TAXAS, EQUIPE E MERCADO PAGO
 */

import { safeApiFetch, saveCurrentUser, getCurrentUser, getAuthHeaders, saveSession, currentToken, currentStore } from './core.api';

export async function fetchStoreSettings() {
    const res = await safeApiFetch('/api/auth/me', { headers: getAuthHeaders() });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Erro ao carregar dados do estabelecimento.');
    return data.store;
}

export async function fetchOnboardingStatus() {
    const res = await safeApiFetch('/api/auth/onboarding-status', { headers: getAuthHeaders() });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Erro ao verificar onboarding.');
    return data;
}

export async function updateStoreSettings(settings) {
    const res = await safeApiFetch('/api/auth/store', {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(settings)
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Erro ao salvar configurações do estabelecimento.');
    if (data.store) {
        saveSession(currentToken, data.store);
    }
    return data;
}

export async function fetchOperatingHours() {
    const res = await safeApiFetch('/api/auth/operating-hours', { headers: getAuthHeaders() });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Erro ao carregar horários de funcionamento.');
    return data.schedule || [];
}

export async function updateOperatingHours(schedule) {
    const res = await safeApiFetch('/api/auth/operating-hours', {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ schedule })
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Erro ao salvar horários de funcionamento.');
    return data.schedule || [];
}

export async function fetchDeliveryFees() {
    const res = await safeApiFetch('/api/auth/delivery-fees', { headers: getAuthHeaders() });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Erro ao carregar taxas por bairro.');
    return data.fees || [];
}

export async function createDeliveryFee(feeData) {
    const res = await safeApiFetch('/api/auth/delivery-fees', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(feeData)
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Erro ao cadastrar taxa de bairro.');
    return data.fee;
}

export async function updateDeliveryFee(id, feeData) {
    const res = await safeApiFetch(`/api/auth/delivery-fees/${id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(feeData)
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Erro ao atualizar taxa de bairro.');
    return data.fee;
}

export async function deleteDeliveryFee(id) {
    const res = await safeApiFetch(`/api/auth/delivery-fees/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Erro ao remover taxa de bairro.');
    return data;
}

export async function toggleStorePause(isPaused, minutes = 30, reason = '') {
    const res = await safeApiFetch('/api/auth/store/pause', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ is_paused: isPaused, minutes, reason })
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Erro ao alterar pausa da loja.');
    return data;
}

// ----------------------------------------------------
// SAAS & MERCADO PAGO / PIX
// ----------------------------------------------------

export async function createPixCharge(storeId, storeName, amount = 97.00) {
    const res = await safeApiFetch('/api/saas/pix-charge', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ storeId, storeName, amount })
    });
    return res.json();
}

export async function verifyPixPayment(storeId, paymentId) {
    const res = await safeApiFetch('/api/saas/verify-pix', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ storeId, paymentId })
    });
    return res.json();
}

export async function activateSupportKey(storeId, key) {
    const res = await safeApiFetch('/api/saas/activate-key', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ storeId, key })
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
        throw new Error(data.error || 'Chave de licença inválida ou inexistente.');
    }
    return data;
}

// Conexão em 1 Clique com Mercado Pago (Restaurante / Pedidos)
export async function fetchStoreMpStatus() {
    const res = await safeApiFetch('/api/saas/mercadopago/status', {
        headers: getAuthHeaders()
    });
    return res.json();
}

export async function getStoreMpConnectUrl() {
    const res = await safeApiFetch('/api/saas/mercadopago/connect-url', {
        headers: getAuthHeaders()
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Erro ao gerar URL de conexão Mercado Pago.');
    return data.url;
}

export async function disconnectStoreMp() {
    const res = await safeApiFetch('/api/saas/mercadopago/disconnect', {
        method: 'POST',
        headers: getAuthHeaders()
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Erro ao desconectar Mercado Pago.');
    return data;
}


// ----------------------------------------------------
// GESTÃO DA EQUIPE (DONO, CAIXA E GARÇOM)
// ----------------------------------------------------

export async function fetchTeamMembers() {
    const res = await safeApiFetch('/api/team', {
        headers: getAuthHeaders()
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Erro ao carregar colaboradores.');
    return data.users || [];
}

export async function createTeamMember({ name, role, pin, email, password }) {
    const res = await safeApiFetch('/api/team', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ name, role, pin, email, password })
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Erro ao adicionar colaborador.');
    return data;
}

export async function updateTeamMember(id, memberData) {
    const res = await safeApiFetch(`/api/team/${id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(memberData)
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Erro ao atualizar colaborador.');
    return data;
}

export async function deleteTeamMember(id) {
    const res = await safeApiFetch(`/api/team/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Erro ao remover colaborador.');
    return data;
}

export async function switchOperatorByPin(pin) {
    const res = await safeApiFetch('/api/team/quick-switch', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ pin })
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'PIN inválido.');
    if (data.token && data.user) {
        saveSession(data.token, currentStore, data.user);
    }
    return data;
}

// ==========================================
// 8. SERVIÇOS MASTER / DESENVOLVEDOR (SAAS BACKOFFICE)
// ==========================================
